import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { Logo } from "@/components/cast/Layout";
import { AuthSky } from "@/components/cast/AuthSky";

function Shell({ title, sub, children }: { title: string; sub: string; children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#FAF7F2] px-6 py-12">
      <AuthSky />
      <div className="relative w-full max-w-md rounded-[2rem] bg-white/70 p-6 shadow-xl ring-1 ring-white/60 backdrop-blur-md sm:p-8" data-testid="reset-card">
        <Logo />
        <h1 className="mt-8 font-display text-4xl font-semibold text-slate-900">{title}</h1>
        <p className="mt-3 text-slate-600">{sub}</p>
        <div className="mt-6">{children}</div>
        <p className="mt-6 text-center text-sm"><Link to="/login" data-testid="reset-back-login" className="font-semibold text-[#C86D51] hover:underline">Back to sign in</Link></p>
      </div>
    </div>
  );
}

const btn = "h-12 w-full rounded-full bg-[#C86D51] text-base font-semibold text-white hover:bg-[#B35A3F]";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  if (sent) return (
    <Shell title="Check your email" sub={`If an account uses ${email}, a link to reset your password is on its way. It works for 1 hour.`}>
      <p data-testid="forgot-sent" className="rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800">Joined with Google or Apple? The same link lets you set a password, so you can also sign in with your email in the Cast app.</p>
    </Shell>
  );
  return (
    <Shell title="Forgot password?" sub="Enter your email and we'll send you a link to reset it, or to set one if you joined with Google or Apple.">
      <form onSubmit={submit} className="space-y-4">
        <div><Label htmlFor="email">Email</Label><Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" className="mt-1.5 h-11 bg-white" /></div>
        {error && <p data-testid="forgot-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        <Button type="submit" disabled={busy} data-testid="forgot-submit" className={btn}>{busy ? "Sending…" : "Send reset link"}</Button>
      </form>
    </Shell>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setError("");
    if (pw !== pw2) return setError("The two passwords don't match");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: pw }); setDone(true); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  if (done) return (
    <Shell title="Password saved" sub="You can now sign in with your email and new password, on the web and in the Cast app.">
      <Link to="/login" data-testid="reset-done-login"><Button className={btn}>Sign in</Button></Link>
    </Shell>
  );
  if (!token) return (
    <Shell title="Link missing" sub="This reset link is incomplete. Ask for a new one below.">
      <Link to="/forgot-password" data-testid="reset-request-new"><Button className={btn}>Get a new link</Button></Link>
    </Shell>
  );
  return (
    <Shell title="Choose a password" sub="Use at least 8 characters.">
      <form onSubmit={submit} className="space-y-4">
        <div><Label htmlFor="pw">New password</Label><Input id="pw" type="password" minLength={8} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" className="mt-1.5 h-11 bg-white" /></div>
        <div><Label htmlFor="pw2">Type it again</Label><Input id="pw2" type="password" minLength={8} required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm" className="mt-1.5 h-11 bg-white" /></div>
        {error && <p data-testid="reset-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error} {error.includes("expired") && <Link to="/forgot-password" className="font-semibold underline">Get a new link</Link>}</p>}
        <Button type="submit" disabled={busy} data-testid="reset-submit" className={btn}>{busy ? "Saving…" : "Save password"}</Button>
      </form>
    </Shell>
  );
}
